// Where a vehicle WOULD be drawn between two fixes. Its own module because it is
// pure arithmetic: no map, no DOM, no timers, so it can be read on its own terms.
//
// Nothing drawn comes from here today. Since #176 the circle is given each
// vehicle's latest fix, and `positionAt`'s one caller — `poll()` in
// `src/data/vehicles.ts` — asks it a full `TWEEN_MS` after `t0`, where it is an
// identity. The module is kept whole, tested and honest for Decision 44 (#96),
// which needs exactly this: the ease, the clamp and the reduced-motion branch.
// A green `knip` does not say otherwise — `knip.json` makes `**/*.test.ts`
// entry points, so `src/shared/tween.test.ts` alone keeps this file reachable.

// A fix arrives every ~10 s; glide to it over the same time.
const TWEEN_MS = 10_000;

/** The two ends of a glide and when it started. */
export type Tween = { from: [number, number]; to: [number, number]; t0: number };

/**
 * Where the vehicle would be along the leg `t` describes, at `now`.
 *
 * Not „where it is drawn": nothing drawn comes from this function. Its one
 * caller uses it to compute a bookkeeping field (`Track.from`), and asks it
 * where it is an identity. An animator is what would make it a drawing
 * function again (#96).
 *
 * Under prefers-reduced-motion the answer is the last fix and nothing else.
 * Sampling the same ease on a one-second interval is the ten-second glide
 * chopped into ten visible jumps — motion still, and rougher than the animation
 * it was meant to replace.
 */
export function positionAt(t: Tween, now: number, reduced: boolean): [number, number] {
  if (reduced) return t.to;
  const k = Math.min(1, (now - t.t0) / TWEEN_MS);
  const e = k < 1 ? 1 - Math.pow(1 - k, 2) : 1; // ease-out: quick start, gentle arrival
  return [t.from[0] + (t.to[0] - t.from[0]) * e, t.from[1] + (t.to[1] - t.from[1]) * e];
}
